import { getSearchHistory, removeFromSearchHistory } from './../helpers'
import { SVG_CLOSE_FILLED, SVG_HISTORY_FILLED } from '@getgo/chameleon-icons'
import { html, property, query } from 'lit-element'
import { nothing } from 'lit-html'
import { repeat } from 'lit-html/directives/repeat'
import { ShellElement } from '../../../common/shell-element'
import searchHistory from './search-history.styles.scss'
import { t } from '../../../directives/translate'
import type { GlobalSearchQueryUI } from '../global-search-models'
import { executeSearch } from '../namespace'
import { unsafeSVG } from 'lit-html/directives/unsafe-svg'
import type { ListboxComponent } from '@getgo/chameleon-web'
import type { ListboxEvent } from '../search-field/global-search-field'

export interface SearchHistoryProps {
  readonly searchHistory: readonly GlobalSearchQueryUI[]
}
export class SearchHistory extends ShellElement implements SearchHistoryProps {
  static readonly tagName = 'goto-search-history'

  @property({ type: Array }) searchHistory: readonly GlobalSearchQueryUI[] = []
  @query('chameleon-listbox') private chameleonListbox: ListboxComponent | undefined

  static get styles() {
    return searchHistory
  }

  public getListbox() {
    return this.chameleonListbox
  }

  protected updated(): void {
    this.dispatchEvent(new CustomEvent<ListboxEvent>('listboxrendered', { detail: { listbox: this.getListbox() } }))
  }

  private async handleClick(event: MouseEvent, query: GlobalSearchQueryUI) {
    event.stopPropagation()
    event.preventDefault()
    await removeFromSearchHistory(query)
    this.searchHistory = await getSearchHistory()
  }

  private handleSearchHistoryItemClick(query: GlobalSearchQueryUI) {
    executeSearch(query)
  }

  private renderSearchQueryItem(query: GlobalSearchQueryUI) {
    return html` <chameleon-listbox-item
      @click=${() => this.handleSearchHistoryItemClick(query)}
      @keydown=${this.handleKeyDownEvent}
      class="search-history-item"
      ><div class="listbox-item-container">
        <chameleon-svg class="history-icon">${unsafeSVG(SVG_HISTORY_FILLED)}</chameleon-svg>
        <chameleon-typography variant="body-medium" class="history-query">${query.query}</chameleon-typography>
        <chameleon-interactive-sub-item label=${t('Delete')}
          ><chameleon-icon-button
            size="small"
            variant="neutral"
            class="delete-search-history-item"
            @click=${(event: MouseEvent) => {
              this.handleClick(event, query)
            }}
          >
            <chameleon-svg>${unsafeSVG(SVG_CLOSE_FILLED)}</chameleon-svg></chameleon-icon-button
          ></chameleon-interactive-sub-item
        >
      </div></chameleon-listbox-item
    >`
  }

  private async handleKeyDownEvent(event: KeyboardEvent) {
    const target: any = event.target
    if (event.key === 'Enter' && target.textContent.trim()) {
      const query = { query: target.textContent.trim() } as GlobalSearchQueryUI
      executeSearch(query)
    }
  }

  render() {
    return html`${this.searchHistory?.length
      ? html`<chameleon-typography
            class="recent-searches-header"
            variant="caption-small-strong"
            color="type-color-secondary"
            >${t('Recent searches')}</chameleon-typography
          >
          <chameleon-listbox class="search-history">
            ${repeat(
              this.searchHistory.slice(0, 5),
              searchQuery => searchQuery,
              searchQuery => this.renderSearchQueryItem(searchQuery),
            )}</chameleon-listbox
          >`
      : nothing}`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [SearchHistory.tagName]: SearchHistory
  }
}
